import React from 'react'

// Constants
import { SKETCH_WEBSITE } from '@sketch/constants'

// Components
import Modal from 'components/Modal'
import { Button } from '@sketch/components'
import { ModalInjectedProps } from 'containers/ModalManager'
import { updateSubscriptionEndInCache } from 'modules/workspace/utils'

// Utils
import { dateFormat } from '@sketch/utils'

// Types
import { useCancelSubscriptionMutation } from '@sketch/gql-types'

interface CancelWorkspaceSubscriptionModalProps extends ModalInjectedProps {
  customerId: string
  workspaceId: string
  nextBillingCycleDate: string
}

export const CancelWorkspaceSubscriptionModal: React.FC<CancelWorkspaceSubscriptionModalProps> = props => {
  const { hideModal, customerId, nextBillingCycleDate, workspaceId } = props

  const [cancelSubscription, { loading }] = useCancelSubscriptionMutation({
    variables: { customerId },
    onError: 'show-toast',
    onCompleted: () => {
      hideModal()
    },
    update: (cache, { data }) => {
      if (!data?.cancelSubscription?.customer) {
        return
      }

      const { subscriptionEnd } = data.cancelSubscription.customer.subscription

      updateSubscriptionEndInCache(cache, {
        workspaceId,
        subscriptionEnd,
      })
    },
  })

  return (
    <Modal title="Cancel your subscription?" onCancel={hideModal}>
      <Modal.Body>
        <p>
          You and other Workspace Members can keep using Sketch until{' '}
          <strong>{dateFormat(new Date(nextBillingCycleDate))}</strong>.
        </p>
        <p>
          After that you’ll lose access to your documents, be unable to manage
          Members — and Editors won’t be able to save documents to this
          Workspace.{' '}
          <a
            href={`${SKETCH_WEBSITE}/support/teams-and-cloud/cancel-subscription`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          disabled={loading}
          onClick={() => {
            hideModal()
          }}
        >
          Keep Plan
        </Button>
        <Button
          variant="negative"
          loading={loading}
          onClick={() => {
            cancelSubscription()
          }}
        >
          Cancel Subscription
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
